import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Box, Button, Paper, Snackbar, Alert, Modal, LinearProgress
} from '@mui/material';
import axios from 'axios';
import {axiosInstance, localAxiosGet, localAxiosPut, localAxiosToken } from '../../../../Axios/axios';
import TokenExpirePopup from '../../../../components/TokenExpirePopup';
import { useNavigate } from 'react-router-dom';


const PolicyAssignment = ({ initialData, navigationContext, userId, onSaveAndReturn, onUpdateEmployeeSuccess, onFormCompletionChange }) => {
  const [assignments, setAssignments] = useState({
    leavePolicy: '',
    shiftType: '',
    attendanceSource: '',
    holidayPolicy: '',
  });
 
  const [leavePolicyTypes, setLeavePloicyTypes] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [attendanceSource, setAttendanceSource] = useState([]);
  const [holidayPolicyTypes, setHolidayPolicyTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completion, setCompletion] = useState(0);

  const Token = localStorage.getItem('accessToken')

  const [openTokenExpirePopup, setOpenTokenExpirePopup] = useState(false);
  const navigate = useNavigate()
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const organizationId = localStorage.getItem('organizationId')

  useEffect(() => {
    if (initialData && leavePolicyTypes.length > 0 && shiftTypes.length > 0 && attendanceSource.length > 0 && holidayPolicyTypes.length > 0) {
      const data = initialData.user || initialData;
      
      setAssignments({
        leavePolicy: data.leave_policy_id || '',
        shiftType: data.shift_type_id || '',
        attendanceSource: data.attendance_source_id || '',
        holidayPolicy: data.holiday_policy_id || '',
      });
    }
  }, [initialData, leavePolicyTypes, shiftTypes, attendanceSource, holidayPolicyTypes]);

  // Calculate form completion percentage
  const calculateProgress = React.useCallback(() => {
    const allFields = ['leavePolicy', 'shiftType', 'attendanceSource', 'holidayPolicy'];
    
    const filledCount = allFields.filter(field => {
      const val = assignments[field];
      return val !== '' && val !== null && val !== undefined;
    }).length;

    const percent = Math.round((filledCount / allFields.length) * 100);
    setCompletion(percent);

    if (typeof onFormCompletionChange === 'function') {
      onFormCompletionChange(percent);
    }
  }, [assignments, onFormCompletionChange]);

  // Recalculate progress when assignments change
  useEffect(() => {
    calculateProgress();
  }, [calculateProgress]);

  // Fetch all policy data from APIs
  useEffect(() => {
    const fetchAllPolicyData = async () => {
      try {
        const leaveResponse = await axiosInstance(localAxiosGet(`/hr/getAllLeavePolicies/${organizationId}`, Token))      

        if (leaveResponse.data.status === 'SUCCESS'){
          setLeavePloicyTypes(leaveResponse.data.result)
        }

        // Fetch Shift Types
        const shiftResponse = await axiosInstance(localAxiosGet(`/hr/getAllShiftTypes/${organizationId}` , Token))

        if (shiftResponse.data.status === 'SUCCESS') {
         setShiftTypes(shiftResponse.data.result)
        }

        // Fetch Attendance Sources
        const attendanceResponse = await axios(localAxiosGet( `/hr/getAllAttendanceSources/${organizationId}`, Token))
        
        if (attendanceResponse.data.status === 'SUCCESS') {
         setAttendanceSource(attendanceResponse.data.result)
        }

        // Fetch Holiday Policies
        const holidayResponse = await axiosInstance(localAxiosGet(`/hr/getAllHolidayPolicies/${organizationId}`, Token))

        if (holidayResponse.data.status === 'SUCCESS') {
          setHolidayPolicyTypes(holidayResponse.data.result)
        }

      } catch (error) {
        console.error('Error fetching policy data:', error);
        if (error.response && (error.response.status === 403 || error.response.statusCode === 403)) {
          setOpenTokenExpirePopup(true);
        }
      } finally {
        
      }
    };

    fetchAllPolicyData();
  }, [organizationId]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseTokenExpirePopup = () => {
    setOpenTokenExpirePopup(false);
    navigate('/');
  };

  // Handler for all policy dropdown changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setAssignments(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveChanges = async () => {
    if(!assignments.leavePolicy || !assignments.shiftType || !assignments.attendanceSource || !assignments.holidayPolicy){
      setSnackbar({
        open:true,
        message:'Required Fields are missing',
        severity:'error'
      })
      return;
    }

    setLoading(true);
    try {
      // Prepare API payload
      const apiPayload = {
        user_id : userId,
        leave_policy_id: assignments.leavePolicy,
        shift_type_id: assignments.shiftType,
        attendance_source_id: assignments.attendanceSource,
        holiday_policy_id: assignments.holidayPolicy,
      };

      try {
          const response = await axiosInstance(localAxiosToken('/hr/updateEmployeePolicyAssignment', apiPayload))

          if (response.data.status === 'SUCCESS') {
              setSnackbar({
                  open: true,
                  message: response.data.message || 'Policy assignments saved successfully!',
                  severity: 'success'
              });
              onUpdateEmployeeSuccess();
              if (navigationContext?.returnToRehire && onSaveAndReturn) {
                  onSaveAndReturn();
              }
              return true;
          } else {
              setSnackbar({
                  open: true,
                  message: response.data.message || 'Failed to update policy assignments',
                  severity: 'error'
              });
              return false;
          }
      } catch (error) {
          console.error('API Error:', error);
          setSnackbar({
              open: true,
              message: error?.data?.message || 'Failed to update policy assignments',
              severity: 'error'
          });
          return false;
      }  

      
    } catch (error) {
      console.error('Error saving policy assignments:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save policy assignments',
        severity: 'error'
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Compact Progress Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" fontWeight={600}>
          Policy Assignment
        </Typography>
        <Box sx={{ width: 220, textAlign: 'right' }}>
          <Typography variant="body2" fontWeight={600}>
            {`Form Completion: ${completion}%`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={completion}
            sx={{
              height: 8,
              borderRadius: 5,
              mt: 0.5,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: completion === 100 ? '#2ecc71' : '#1976d2'
              }
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* 1. Leave Policy Dropdown */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            required
            label="Leave Policy"
            name="leavePolicy"
            value={assignments.leavePolicy}
            onChange={handleChange}
            variant="outlined" 
            disabled={loading}         
           >
            {leavePolicyTypes.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.leave_policy_name}
                </MenuItem>
              ))
            }
          </TextField>
        </Grid>

        {/* 2. Shift Type Dropdown */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            required
            label="Shift Type"
            name="shiftType"
            value={assignments.shiftType}
            onChange={handleChange}
            variant="outlined"
            disabled={loading}
          >          
              {shiftTypes.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.shift_name}
                </MenuItem>
              ))
            }
          </TextField>
        </Grid>

        {/* 3. Attendance Source Dropdown */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            required
            label="Attendance Source"
            name="attendanceSource"
            value={assignments.attendanceSource}
            onChange={handleChange}
            variant="outlined" 
            disabled={loading}         
          >
           {attendanceSource.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.source_name}
                </MenuItem>
              ))
            }
          </TextField>
        </Grid>

        {/* 4. Holiday Policy Dropdown */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            required
            label="Holiday Policy"
            name="holidayPolicy"
            value={assignments.holidayPolicy}
            onChange={handleChange}
            variant="outlined"
            disabled={loading}
          >
          {holidayPolicyTypes.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.holiday_policy_name}
                </MenuItem>
              ))
            }
          </TextField>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        {navigationContext?.returnToRehire ? (
          <Paper
            elevation={3}
            sx={{
              width: '100%',
              p: 2,
              bgcolor: 'info.light',
              borderLeft: '4px solid',
              borderColor: 'info.main'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Rehire Verification in Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completing verification for: <strong>{navigationContext.stepLabel || 'Policy Assignment'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Form Completion: <strong>{completion}%</strong>
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="info"
                onClick={async () => {
                  const success = await handleSaveChanges();
                  if (success && onSaveAndReturn) {
                    onSaveAndReturn();
                  }
                }}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Verify & Return to Rehire'}
              </Button>
            </Box>
          </Paper>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveChanges}
            sx={{ py: 1.5, px: 4 }}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Details'}
          </Button>
        )}
      </Box>

      <Modal
        open={openTokenExpirePopup}
        onClose={handleCloseTokenExpirePopup}
        aria-labelledby="token-expire-modal-title"
        aria-describedby="token-expire-modal-description"
      >
        <TokenExpirePopup onClose={handleCloseTokenExpirePopup} />
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PolicyAssignment;